import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const NAV_LINKS = [
  { key: "features", href: "#features" },
  { key: "howItWorks", href: "#how-it-works" },
  { key: "faq", href: "#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ja' ? 'en' : 'ja';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLinkClick = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "glass-card-strong"
          : "bg-transparent"
      }`}
      style={scrolled ? { boxShadow: "0 2px 24px rgba(0,0,0,0.06)" } : {}}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="flex items-center gap-2 cursor-pointer flex-shrink-0"
        >
          <img
            src="/logo.png"
            alt="Visipo Logo"
            className="h-8 w-auto object-contain"
          />
          <span
            className={`font-bold text-xl tracking-wide transition-colors duration-300 ${scrolled ? "text-gray-900" : "text-white"}`}
            style={{ fontFamily: "'Inter', 'Noto Sans JP', sans-serif" }}
          >
            Visipo
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleLinkClick(link.href); }}
              className={`text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap hover:text-visipo ${
                scrolled ? "text-gray-700" : "text-white/90"
              }`}
            >
              {t(`navbar.${link.key}`)}
            </a>
          ))}
        </nav>

        {/* Right side: Lang Switcher & Mobile Menu */}
        <div className="flex items-center gap-4">
          <button
            onClick={toggleLanguage}
            className={`text-sm font-bold px-3 py-1.5 rounded-full transition-colors duration-300 ${
              scrolled
                ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
            }`}
          >
            {i18n.language === 'ja' ? 'EN' : 'JP'}
          </button>
        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex items-center justify-center w-10 h-10 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="メニュー"
        >
          <i
            className={`ri-${menuOpen ? "close" : "menu"}-line text-2xl ${scrolled ? "text-gray-900" : "text-white"}`}
          />
        </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-400 ${
          menuOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0"
        }`}
        style={{ background: "rgba(255,255,255,0.97)", backdropFilter: "blur(12px)" }}
      >
        <div className="px-4 py-4 flex flex-col gap-3 border-t border-gray-100">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleLinkClick(link.href); }}
              className="text-gray-700 font-medium py-2 hover:text-[#13B6EC] transition-colors cursor-pointer"
            >
              {t(`navbar.${link.key}`)}
            </a>
          ))}
        </div>
      </div>
    </header>
  );
}
